import { Dispatch, SetStateAction, useEffect } from 'react';

import {
  EmployeeIdentificationType,
  EmployeeViewModel,
  EmploymentViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import {
  Assets,
  Box,
  Flex,
  Headings,
  Link,
  RadioButton,
  SpeechBubble,
  Text,
  useRadioButtonWithValidate,
} from '@foradevelopment/fora-ui';

import { useTypedTranslation } from '../../i18n/i18n';

export const NEW_EMPLOYMENT = 'NEW_EMPLOYMENT';

export const PartialDuplicateForm = ({
  partialDuplicateOptions,
  partialDuplicateRadioInput,
  setSelectedDuplicateEmployment,
  employmentsLink,
}: {
  partialDuplicateOptions: EmploymentViewModel[];
  partialDuplicateRadioInput: ReturnType<typeof useRadioButtonWithValidate>;
  setSelectedDuplicateEmployment: Dispatch<
    SetStateAction<{ employee: EmployeeViewModel; isInactive: boolean; policyNumber: string } | null>
  >;
  employmentsLink: string;
}) => {
  const { t } = useTypedTranslation();

  useEffect(() => {
    return () => {
      setSelectedDuplicateEmployment(null);
      partialDuplicateRadioInput.setValue('');
    };
    // Empty as intended, to make the unmount function only run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = ({
    employee,
    event,
    isInactive,
    policyNumber,
  }: {
    employee: EmployeeViewModel | null;
    event: React.ChangeEvent<HTMLInputElement>;
    isInactive: boolean;
    policyNumber: string;
  }) => {
    if (
      employee?.identificationType === EmployeeIdentificationType.PersonalIdentityNumber ||
      employee?.identificationType === EmployeeIdentificationType.CoOrdinationNumber
    ) {
      setSelectedDuplicateEmployment({ employee, isInactive, policyNumber });
    } else {
      setSelectedDuplicateEmployment(null);
    }
    partialDuplicateRadioInput.onChange(event);
  };

  return (
    <SpeechBubble marginTop={30}>
      <Headings.H5>{t('form.handleDuplicate.title')}</Headings.H5>
      <Text marginTop={10}>{t('form.handleDuplicate.description')}</Text>
      <Flex flexDirection="column" rowGap={20} role="radiogroup" marginTop={20}>
        {partialDuplicateOptions?.map(({ employee, policyNumber, isInactive }) => {
          const selected = employee.employeeId === partialDuplicateRadioInput.value;
          return (
            <Flex flexDirection="column" key={employee.employeeId}>
              <RadioButton
                key={employee.employeeId}
                id={employee.employeeId}
                name="duplicate-option"
                value={employee.employeeId}
                checked={selected}
                onChange={event => handleChange({ event, isInactive, employee, policyNumber })}
                label={t('form.handleDuplicate.label', {
                  employeeName: `${employee.firstName} ${employee.lastName}`,
                  employeeIDType:
                    employee.identificationType === EmployeeIdentificationType.CoOrdinationNumber
                      ? t('common.coordinationNumber')
                      : t('common.personalNumber'),
                  fullBirthDate: `${employee.employeeId.slice(0, -4)}-${employee.employeeId.slice(-4)},`,
                })}
              />
              {selected && isInactive && (
                <FeedbackText text={t('form.handleDuplicate.selectedInactiveEmploymentWarning')} />
              )}
              {selected && !isInactive && (
                <Box>
                  <FeedbackText text={t('form.handleDuplicate.selectedActiveEmploymentWarning')} />
                  <Box marginLeft={[40, 40, 30]} marginTop={10}>
                    <Link styleAs="button" to={employmentsLink} iconLeft>
                      {t('common.back')}
                    </Link>
                  </Box>
                </Box>
              )}
            </Flex>
          );
        })}
        <RadioButton
          key={NEW_EMPLOYMENT}
          id={NEW_EMPLOYMENT}
          name="new-employment-option"
          value={NEW_EMPLOYMENT}
          checked={NEW_EMPLOYMENT === partialDuplicateRadioInput.value}
          onChange={event => handleChange({ event, employee: null, isInactive: false, policyNumber: '' })}
          label={t('form.handleDuplicate.newEmployment')}
        />
        {partialDuplicateRadioInput.showError && (
          <Flex>
            <Assets.TriangleExclamationIcon marginRight={10} color="burgundy" />
            <Text fontWeight="semibold" color="burgundy">
              {t('form.handleDuplicate.hasNotMatchedErrorText')}
            </Text>
          </Flex>
        )}
      </Flex>
    </SpeechBubble>
  );
};

const FeedbackText = ({ text }: { text: string }) => {
  return (
    <Text color="black70" marginLeft={[40, 40, 30]} marginTop={4}>
      {text}
    </Text>
  );
};
